import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Mail, Phone, MapPin, Clock, Send, MessageCircle, Building2, Stethoscope, HelpCircle, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    category: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setLoading(false);
    setSubmitted(true);
    toast.success('Votre message a été envoyé avec succès !');
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email',
      value: 'contact@dockhta.com',
      description: 'Réponse sous 24-48h',
      href: 'mailto:contact@dockhta.com'
    },
    {
      icon: Phone,
      title: 'Téléphone',
      value: '+237 621 05 53 65',
      description: 'Lun-Ven : 8h-18h',
      href: 'tel:+237621055365'
    },
    {
      icon: MapPin,
      title: 'Adresse',
      value: 'Yaoundé, Cameroun',
      description: 'Siège social',
      href: null
    },
    {
      icon: Clock,
      title: 'Horaires',
      value: '8h00 - 18h00',
      description: 'Du lundi au vendredi',
      href: null
    }
  ];

  const categories = [
    { value: 'patient', label: 'Je suis patient', icon: HelpCircle },
    { value: 'professional', label: 'Je suis professionnel de santé', icon: Stethoscope },
    { value: 'facility', label: 'Je représente un établissement', icon: Building2 },
    { value: 'partnership', label: 'Partenariat / Presse', icon: MessageCircle },
    { value: 'other', label: 'Autre demande', icon: Mail }
  ];

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50">
        <header className="bg-white border-b sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/">
              <Button variant="ghost" className="gap-2">
                <ArrowLeft className="w-4 h-4" />
                Retour à l&apos;accueil
              </Button>
            </Link>
            <Link to="/" className="text-2xl font-bold text-[#E53935]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              DOCKHTA
            </Link>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-4 py-24 text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-emerald-600" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Message envoyé !
          </h1>
          <p className="text-slate-600 mb-8">
            Merci de nous avoir contactés. Notre équipe vous répondra dans les plus brefs délais.
          </p>
          <Link to="/">
            <Button className="bg-[#E53935] hover:bg-[#D32F2F]">
              Retour à l&apos;accueil
            </Button>
          </Link>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/">
            <Button variant="ghost" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Retour à l&apos;accueil
            </Button>
          </Link>
          <Link to="/" className="text-2xl font-bold text-[#E53935]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            DOCKHTA
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-6xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Contactez-nous
          </h1>
          <p className="text-slate-600 max-w-2xl mx-auto">
            Une question, une suggestion ou besoin d&apos;aide ? Notre équipe est là pour vous accompagner.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Info */}
          <div className="lg:col-span-1 space-y-4">
            {contactInfo.map((info, index) => {
              const IconComponent = info.icon;
              return (
                <Card key={index} className="border-0 shadow-sm">
                  <CardContent className="p-4 flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#E53935]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-5 h-5 text-[#E53935]" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">{info.title}</p>
                      {info.href ? (
                        <a href={info.href} className="font-semibold text-slate-900 hover:text-[#E53935]">
                          {info.value}
                        </a>
                      ) : (
                        <p className="font-semibold text-slate-900">{info.value}</p>
                      )}
                      <p className="text-xs text-slate-500 mt-0.5">{info.description}</p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            {/* FAQ Link */}
            <Card className="border-0 shadow-sm bg-gradient-to-br from-[#E53935]/5 to-[#E53935]/10">
              <CardContent className="p-6 text-center">
                <HelpCircle className="w-8 h-8 text-[#E53935] mx-auto mb-3" />
                <h3 className="font-semibold text-slate-900 mb-2">Questions fréquentes</h3>
                <p className="text-sm text-slate-600 mb-4">
                  Consultez notre FAQ pour des réponses rapides
                </p>
                <Link to="/faq">
                  <Button variant="outline" className="w-full">
                    Voir la FAQ
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-6 md:p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Nom complet *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Votre nom"
                        className="mt-1"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="votre@email.com"
                        className="mt-1"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="phone">Téléphone</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="+237 6XX XXX XXX"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="category">Catégorie</Label>
                      <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Sélectionner..." />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((cat) => (
                            <SelectItem key={cat.value} value={cat.value}>
                              {cat.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="subject">Sujet</Label>
                    <Input
                      id="subject"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      placeholder="L'objet de votre message"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Décrivez votre demande en détail..."
                      className="mt-1"
                      rows={6}
                      required
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-[#E53935] hover:bg-[#D32F2F] h-12"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Envoyer le message
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Contact;
